/**
 * Temporary screen-scoped design tokens for **Settings → Roles**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Roles" section) 1:1 — every
 * colour, size, radius, shadow, spacing and type step the roles list needs is
 * captured here so the components never carry a raw literal. These stay
 * screen-local on purpose: once every settings screen has been re-skinned the
 * shared values get promoted into the MUI theme and this file is deleted.
 */

import { contentCard, formField, inkfree, signer, switchControl, white } from "theme/colors";

export const rolesTokens = {
  color: {
    /** Deep brand ink — role names. */
    ink: signer.ink,
    /** Resource label beside each permission group. */
    resourceLabel: signer.text.meta,
    /** Column-header strip text. */
    columnHeader: signer.text.faint,
    surface: white,
    /** Card chrome. */
    cardBorder: contentCard.borderMuted,
    /** Column-header strip fill + its bottom rule / footer top rule. */
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    /** Rule between role rows. */
    rowDivider: signer.border.divider,
    /** Permission pill. */
    permissionText: signer.orange.dark,
    permissionBg: inkfree.light,
    /** Hover text / focus ring — a thin accent, stays orange. */
    accent: signer.orange.main,
    /**
     * Primary action ("Create Role") + active page item — solid fills, so
     * per the standing brand-colour rule these are the app brand red, not
     * `accent` above.
     */
    ctaBg: inkfree.main,
    ctaBgHover: inkfree.dark,
    onAccent: white,
    /** Inactive pagination item. */
    pageItem: signer.text.meta,
    pageHoverFill: signer.surface.muted,
    /** Disabled pagination arrow. */
    pageDisabled: switchControl.trackOff,
    emptyText: signer.text.meta,
  },

  font: {
    columnHeader: "10.5px",
    roleName: "13px",
    resourceLabel: "12px",
    permission: "11.5px",
    button: "13px",
    pageItem: "12px",
    empty: "12.5px",
  },

  weight: {
    strong: 700,
    medium: 600,
    regular: 400,
  },

  letterSpacing: {
    columnHeader: "0.09em",
  },

  radius: {
    card: "14px",
    control: "9px",
    pageItem: "7px",
    pill: "999px",
  },

  border: {
    hairline: "1px",
  },

  shadow: {
    card: "0 1px 3px rgba(33, 21, 71, 0.04)",
  },

  space: {
    /** Horizontal padding shared by the header strip, every row and the footer. */
    gutter: "28px",
    columnHeaderY: "10px",
    rowY: "15px",
    footerY: "14px",
    /** Gap between a resource label and its pills, and between pills. */
    permissionGap: "8px",
    /** Gap between the stacked resource lines of a multi-resource role. */
    resourceRowGap: "8px",
    permissionPaddingY: "3px",
    permissionPaddingX: "11px",
    paginationGap: "4px",
    /** Optical nudge that lines the role name up with its first pill row. */
    nameOffsetTop: "3px",
    emptyY: "34px",
  },

  size: {
    buttonHeight: "40px",
    buttonPaddingX: "20px",
    /** Fixed column the resource label occupies inside each permission line. */
    resourceLabelWidth: "76px",
    pageItem: "28px",
    pageArrow: "11px",
    pageArrowStrokeWidth: 2.5,
  },

  /** Grid template copied verbatim from the design. */
  grid: {
    row: "220px 1fr",
  },

  /**
   * Create / Edit Role form. The Settings design has no mock for this screen,
   * so it is composed from the language the neighbouring settings screens
   * already use: the Delegation card's 40px field chrome, the Roles list card,
   * and the same accent for the primary action.
   */
  form: {
    cardPadding: "24px 28px",
    /** Gap between the two labelled sections inside the card. */
    sectionGap: "22px",
    sectionTitleFontSize: "15px",
    sectionTitleWeight: 800,
    sectionTitleLetterSpacing: "-0.01em",
    sectionDescriptionColor: signer.text.meta,
    sectionDescriptionFontSize: "12.5px",
    sectionDescriptionLineHeight: 1.55,
    sectionHeaderGap: "3px",
    sectionBodyGap: "14px",
    /** Rule between sections and above the button row. */
    divider: signer.border.light,

    labelGap: "6px",
    labelFontSize: "11.5px",
    labelWeight: 600,
    labelColor: signer.ink,
    requiredColor: signer.orange.main,

    fieldHeight: 40,
    fieldRadius: "9px",
    fieldBorder: formField.border,
    fieldBg: formField.bg,
    fieldColor: signer.ink,
    fieldFontSize: "13px",
    fieldWeight: 500,
    fieldPaddingX: "12px",
    fieldFocusBorder: signer.orange.main,
    fieldPlaceholder: formField.placeholderMuted,
    caretColor: signer.text.faint,
    errorFontSize: "10.5px",
    /** Role name reads as a short field, not a full-bleed one. */
    nameFieldMaxWidth: 420,

    /** One resource ↔ permissions pairing. */
    resourceRowBg: formField.bg,
    resourceRowBorder: signer.border.light,
    resourceRowRadius: "11px",
    resourceRowPadding: "14px 16px",
    resourceRowGap: "12px",
    resourceColumnGap: "14px",
    /** Square that carries the remove-resource glyph. */
    removeSize: 36,
    removeRadius: "9px",
    removeColor: signer.text.faint,
    removeHoverColor: signer.danger.main,
    removeHoverBg: signer.danger.tint,
    removeIconSize: 15,

    /** Dashed "Add another resource" affordance closing the list. */
    addHeight: 40,
    addRadius: "9px",
    addBorder: "#E3D6C9",
    addColor: signer.orange.dark,
    addHoverBg: inkfree.light,
    addFontSize: "12.5px",
    addGap: "8px",
    addIconSize: 14,

    footerGap: "10px",
    footerPaddingTop: "18px",
    /** Neutral "Cancel" beside the filled primary. */
    secondaryBg: white,
    secondaryBorder: formField.border,
    secondaryColor: signer.text.secondary,
    secondaryHoverBorder: signer.ink,
    secondaryHoverColor: signer.ink,
  },
} as const;

/**
 * Phone and tablet values for **Settings → Roles**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375, 390 and 834 identically — only the gutters around
 * the cards change, and `SettingsLayout` already owns those. Applied below
 * `md`; the desktop table is untouched.
 */
export const rolesResponsiveTokens = {
  /** Full-width "Create Role" above the list. */
  action: { height: "44px", paddingX: "20px", fontSize: "13px" },
  /** One role, drawn as its own card instead of a table row. */
  card: {
    border: contentCard.borderMuted,
    radius: "14px",
    padding: "15px 16px",
    gap: "11px",
    headGap: "10px",
    nameFontSize: "14px",
    chevronSize: 14,
    chevronColor: signer.border.strong,
    groupGap: "6px",
    labelFontSize: "10px",
    labelLetterSpacing: "0.09em",
    pillGap: "6px",
    pillPaddingY: "4px",
  },
  /** Centred pager under the cards; no strip, no top rule. */
  pager: {
    itemSize: "34px",
    itemRadius: "9px",
    fontSize: "12.5px",
    gap: "6px",
    paddingY: "4px",
    idleBorder: formField.border,
  },
} as const;

export default rolesTokens;
